import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { searchApi } from '../../services/api';
import './GlobalSearch.css';

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);
  const cacheRef = useRef(new Map());

  const [isOpen, setIsOpen] = useState(false);
  const [searchType, setSearchType] = useState('fighter');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [errorMessage, setErrorMessage] = useState('');

  const trimmedQuery = query.trim();

  const getCacheKey = (type, nextQuery) => `${type}:${nextQuery.trim().toLowerCase()}`;

  const closeSearch = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const resetSearch = () => {
    setQuery('');
    setResults([]);
    setStatus('idle');
    setHighlightedIndex(-1);
    setErrorMessage('');
  };

  const openSearch = () => {
    setIsOpen(true);
  };

  const handleToggleSearch = () => {
    if (isOpen) {
      closeSearch();
      return;
    }

    openSearch();
  };

  const handleResultSelect = (result) => {
    const destination = result.resultType === 'event'
      ? `/event/${result.id}/fights`
      : `/fighter/${result.id}`;

    navigate(destination);
    closeSearch();
    resetSearch();
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    inputRef.current?.focus();
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    closeSearch();
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeSearch();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setStatus('idle');
      setHighlightedIndex(-1);
      setErrorMessage('');
      return undefined;
    }

    const cacheKey = getCacheKey(searchType, trimmedQuery);
    const cachedResults = cacheRef.current.get(cacheKey);

    if (cachedResults) {
      setResults(cachedResults);
      setStatus(cachedResults.length > 0 ? 'success' : 'empty');
      setHighlightedIndex(-1);
      setErrorMessage('');
      return undefined;
    }

    const abortController = new AbortController();
    const requestId = requestIdRef.current + 1;

    requestIdRef.current = requestId;
    setStatus('loading');
    setErrorMessage('');

    const timeoutId = window.setTimeout(async () => {
      try {
        const nextResults = await searchApi.search({
          type: searchType,
          query: trimmedQuery,
          signal: abortController.signal,
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        cacheRef.current.set(cacheKey, nextResults);
        setResults(nextResults);
        setStatus(nextResults.length > 0 ? 'success' : 'empty');
        setHighlightedIndex(-1);
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        if (requestId !== requestIdRef.current) {
          return;
        }

        setResults([]);
        setStatus('error');
        setHighlightedIndex(-1);
        setErrorMessage(error.message || 'Unable to load search results.');
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [isOpen, searchType, trimmedQuery]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeSearch();
      return;
    }

    if (status !== 'success' || results.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((currentIndex) => (
        currentIndex < results.length - 1 ? currentIndex + 1 : 0
      ));
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((currentIndex) => (
        currentIndex > 0 ? currentIndex - 1 : results.length - 1
      ));
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const selectedIndex = highlightedIndex >= 0 ? highlightedIndex : 0;
      const selectedResult = results[selectedIndex];

      if (selectedResult) {
        handleResultSelect(selectedResult);
      }
    }
  };

  const renderResults = () => {
    if (status === 'loading') {
      return <p className="global-search-message">Searching {searchType}s...</p>;
    }

    if (status === 'error') {
      return <p className="global-search-message global-search-message-error">{errorMessage}</p>;
    }

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      return (
        <p className="global-search-message">
          Type at least {MIN_QUERY_LENGTH} characters to search {searchType}s.
        </p>
      );
    }

    if (status === 'empty') {
      return <p className="global-search-message">No {searchType} results found.</p>;
    }

    if (status !== 'success') {
      return null;
    }

    return (
      <ul className="global-search-results" role="listbox">
        {results.map((result, index) => {
          const isHighlighted = index === highlightedIndex;

          return (
            <li key={`${result.resultType}-${result.id}`}>
              <button
                className={`global-search-result ${isHighlighted ? 'is-highlighted' : ''}`}
                onClick={() => handleResultSelect(result)}
                onMouseEnter={() => setHighlightedIndex(index)}
                type="button"
              >
                <span className="global-search-result-name">{result.name}</span>
                <span className="global-search-result-type">{result.resultType}</span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="global-search" ref={containerRef}>
      <button
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={`global-search-toggle ${isOpen ? 'is-open' : ''}`}
        onClick={handleToggleSearch}
        type="button"
      >
        <span className="global-search-toggle-icon">
          Search
        </span>
      </button>

      {isOpen && (
        <div className="global-search-panel" role="dialog" aria-label="Global search">
          <div className="global-search-type-toggle" role="group" aria-label="Search type">
            <button
              className={searchType === 'fighter' ? 'is-active' : ''}
              onClick={() => setSearchType('fighter')}
              type="button"
            >
              Fighters
            </button>
            <button
              className={searchType === 'event' ? 'is-active' : ''}
              onClick={() => setSearchType('event')}
              type="button"
            >
              Events
            </button>
          </div>

          <label className="global-search-input-shell">
            <span className="visually-hidden">Search</span>
            <input
              className="global-search-input"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Search ${searchType}s...`}
              ref={inputRef}
              type="text"
              value={query}
            />
          </label>

          <div className="global-search-dropdown">
            {renderResults()}
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
